/**
 * Reticle tracking and tap-to-place.
 *
 * IWSDK's `EnvironmentRaycastTarget` does the hard part: it positions the host
 * object at the real-world hit point, orients its +Y to the surface normal, and
 * hides it when there is no hit — every frame, with no room scan required.
 *
 * What this system adds is the *feel*:
 *  - a short scan delay, so the reticle does not appear during the noisy first
 *    moments of tracking;
 *  - a stability gate, so a pinch only commits after the surface has held for
 *    several consecutive frames. This is most of the difference between
 *    placement feeling solid and feeling twitchy.
 *
 * Note the pointer ray you can see in the headset is a *different* subsystem —
 * it only intersects virtual objects and will happily pass through a real
 * table. Placement must come from the raycast target, never the ray pointer.
 */
import {
  EnvironmentRaycastTarget,
  Quaternion,
  RaycastSpace,
  Vector3,
  createSystem,
} from '@iwsdk/core';

import { CONFIG } from '../config.js';
import { createReticle, type Reticle } from './reticle.js';
import { placementStore, type Pose } from './placementStore.js';

export class PlacementSystem extends createSystem({}) {
  private reticle?: Reticle;
  private reticleEntity?: ReturnType<typeof this.world.createTransformEntity>;
  private stableFrames = 0;
  private sessionElapsed = 0;
  private inSession = false;
  /** Guards against a pinch being counted by both the poll and the backstop. */
  private commitGuard = false;

  private readonly worldPosition = new Vector3();
  private readonly worldQuaternion = new Quaternion();

  override init(): void {
    this.reticle = createReticle();
    this.reticleEntity = this.world.createTransformEntity(this.reticle.group);
    this.reticleEntity.addComponent(EnvironmentRaycastTarget, {
      // Right hand by default. Quest reports whichever input is active, and the
      // component retries until the source connects, so this works for a hand
      // or a controller with no branching.
      space: RaycastSpace.Right,
      maxDistance: CONFIG.placement.maxDistanceMeters,
    });

    const xr = this.renderer.xr;
    const onStart = (): void => {
      this.inSession = true;
      this.sessionElapsed = 0;
      this.stableFrames = 0;
      placementStore.patch({ phase: 'scanning', locked: false });
      this.attachSelectBackstop();
    };
    const onEnd = (): void => {
      this.inSession = false;
      placementStore.patch({ phase: 'idle', locked: false });
    };
    xr.addEventListener('sessionstart', onStart);
    xr.addEventListener('sessionend', onEnd);

    this.cleanupFuncs.push(() => {
      xr.removeEventListener('sessionstart', onStart);
      xr.removeEventListener('sessionend', onEnd);
      this.reticle?.dispose();
    });
  }

  override update(delta: number, time: number): void {
    if (!this.inSession || !this.reticle || !this.reticleEntity) return;

    this.sessionElapsed += delta;
    const state = placementStore.getSnapshot();
    const wantsReticle = state.phase === 'scanning' || state.phase === 'targeting';

    if (!wantsReticle) {
      this.reticle.group.visible = false;
      return;
    }

    // Suppress the reticle entirely while tracking settles. The raycast target
    // sets `visible` itself, so override it rather than fighting it.
    if (this.sessionElapsed < CONFIG.placement.scanDelaySeconds) {
      this.reticle.group.visible = false;
      this.stableFrames = 0;
      return;
    }

    const hit = this.reticleEntity.getValue(EnvironmentRaycastTarget, 'xrHitTestResult');
    const onSurface = Boolean(hit) && this.reticle.group.visible;

    if (onSurface) {
      this.stableFrames += 1;
      this.reticle.updateBob(time);
      if (state.phase === 'scanning') {
        placementStore.patch({ phase: 'targeting' });
      }
    } else {
      // Reset rather than decay: a dropped hit means the surface moved out from
      // under the user, and re-confirming from scratch is the honest behaviour.
      this.stableFrames = 0;
    }

    const locked = onSurface && this.stableFrames >= CONFIG.placement.minStableFrames;
    if (locked !== state.locked) {
      placementStore.patch({ locked });
    }

    if (locked && this.consumePinch()) {
      this.commit();
    }
  }

  /**
   * Poll both hands for a select edge. `getSelectStart()` unifies hand-pinch
   * and controller-trigger — for a hand input source the profile maps the pinch
   * onto gamepad button 0, so no branching is needed.
   */
  private consumePinch(): boolean {
    if (this.commitGuard) return false;
    const gamepads = this.input.xr.gamepads;
    return Boolean(gamepads.right?.getSelectStart() || gamepads.left?.getSelectStart());
  }

  /**
   * Backstop for runtimes that do not synthesise a gamepad for a tracked hand,
   * where the poll above would never see an edge. WebXR's own `select` fires
   * for pinch and trigger alike, and IWSDK attaches no listener of its own, so
   * there is nothing to double-fire against except our own poll — hence the
   * one-frame guard.
   */
  private attachSelectBackstop(): void {
    const session = this.world.xrSession;
    if (!session) return;
    const onSelect = (): void => {
      const { phase, locked } = placementStore.getSnapshot();
      if (!locked || (phase !== 'targeting' && phase !== 'scanning')) return;
      this.commit();
    };
    session.addEventListener('select', onSelect);
    this.cleanupFuncs.push(() => session.removeEventListener('select', onSelect));
  }

  private commit(): void {
    const object = this.reticleEntity?.object3D;
    if (!object) return;

    // The reticle IS the placement transform — read its resolved world pose
    // rather than re-querying the hit test, so what the user saw is what they
    // get even if tracking shifts between the frame and the commit.
    object.getWorldPosition(this.worldPosition);
    object.getWorldQuaternion(this.worldQuaternion);

    const pose: Pose = {
      position: [this.worldPosition.x, this.worldPosition.y, this.worldPosition.z],
      quaternion: [
        this.worldQuaternion.x,
        this.worldQuaternion.y,
        this.worldQuaternion.z,
        this.worldQuaternion.w,
      ],
    };

    this.commitGuard = true;
    // Release on the next tick so a single physical pinch cannot be consumed
    // twice by the poll and the backstop.
    queueMicrotask(() => {
      this.commitGuard = false;
    });

    this.reticle!.group.visible = false;
    this.stableFrames = 0;
    placementStore.patch({ phase: 'placed', pose, locked: false });
  }
}
